import { PlanType } from "@brookbush/database";
import {
	isJoiningPriceVariant,
	type JoiningPriceVariant,
	MEMBERSHIP_JOINING_FEE_CENTS,
	MEMBERSHIP_JOINING_PRICE_MODEL,
	MEMBERSHIP_PRICE_IDS,
	type PlanConfigExpanded,
	type TrialLength,
} from "@brookbush/types";

import { format } from "date-fns";
import type Stripe from "stripe";

import {
	convertToDollars,
	formatPrice,
	getAmountIndividual,
	getAmountIndividualAfterDiscount,
	getFirstBillingDate,
} from "./stripe/helpers";

export const MEMBERSHIP_JOINING_PRICE_FLAG = "membership-joining-price";

const JOINING_PRICE_OFFER_KEY = "membership-joining-price-offer";

export type JoiningPriceOffer = {
	model: typeof MEMBERSHIP_JOINING_PRICE_MODEL;
	variant: JoiningPriceVariant;
	/** One-time fee in dollars added to the first monthly payment. */
	joiningFee: number;
};

export type JoiningPriceCopy = {
	headline: string;
	/** Annual savings line; null on monthly or when annual saves nothing. */
	subline: string | null;
	button: string;
	/** Only set for trials. */
	checkout: string | null;
};

const joiningFeeFor = (variant: JoiningPriceVariant): number =>
	variant === "control"
		? 0
		: convertToDollars(MEMBERSHIP_JOINING_FEE_CENTS[variant]);

const toOffer = (variant: JoiningPriceVariant): JoiningPriceOffer => ({
	model: MEMBERSHIP_JOINING_PRICE_MODEL,
	variant,
	joiningFee: joiningFeeFor(variant),
});

export const CONTROL_JOINING_PRICE_OFFER = toOffer("control");

/**
 * Picks the visitor's joining-price arm. Fees are USD-only, so other
 * currencies always get control.
 *
 * @param {object} input
 * @param {unknown} input.flagValue - `membership-joining-price` flag value
 * @param {string | string[] | null} [input.devQuery] - `?joining_price=`, honoured in development only
 * @param {string} input.nodeEnv - `process.env.NODE_ENV`
 * @param {string | null} [input.currency] - Checkout currency, USD when missing
 * @returns {JoiningPriceOffer} The arm and its fee
 */
export const resolveJoiningPrice = (input: {
	flagValue: unknown;
	devQuery?: string | string[] | null;
	nodeEnv: string;
	currency?: string | null;
}): JoiningPriceOffer => {
	if ((input.currency ?? "usd").toLowerCase() !== "usd") {
		return CONTROL_JOINING_PRICE_OFFER;
	}

	const devValue =
		input.nodeEnv === "development" && input.devQuery != null
			? Array.isArray(input.devQuery)
				? input.devQuery[0]
				: input.devQuery
			: undefined;

	if (isJoiningPriceVariant(devValue)) return toOffer(devValue);
	if (isJoiningPriceVariant(input.flagValue)) return toOffer(input.flagValue);
	return CONTROL_JOINING_PRICE_OFFER;
};

/**
 * Brent's plan-card, button and trial copy for one arm and interval.
 * Amounts are in dollars and already discounted when a promo code applies.
 *
 * @param {object} input
 * @param {JoiningPriceOffer} input.offer - Visitor's arm
 * @param {"month" | "year"} input.interval - Plan interval being described
 * @param {number} input.monthlyAmount - Monthly membership price
 * @param {number} input.yearlyAmount - Annual membership price
 * @param {string | null} [input.currency] - Display currency, USD when missing
 * @param {string} [input.trialEndsLabel] - Trial end date; switches checkout to trial copy
 * @returns {JoiningPriceCopy} Strings for every surface
 */
export const getJoiningPriceCopy = (input: {
	offer: JoiningPriceOffer;
	interval: "month" | "year";
	monthlyAmount: number;
	yearlyAmount: number;
	currency?: string | null;
	trialEndsLabel?: string;
}): JoiningPriceCopy => {
	const { offer, interval, monthlyAmount, yearlyAmount, trialEndsLabel } =
		input;
	const format = (value: number) =>
		formatPrice({ currency: input.currency, value });

	const isJoining = offer.joiningFee > 0;
	const firstPayment = +(monthlyAmount + offer.joiningFee).toFixed(2);
	const monthly = format(monthlyAmount);
	const yearly = format(yearlyAmount);
	const first = format(firstPayment);
	const trialStart = trialEndsLabel
		? `Your free trial ends ${trialEndsLabel} so you pay ${format(0)} today`
		: null;

	if (interval === "month") {
		return {
			headline: isJoining
				? `First month: ${first}, then ${monthly}/month`
				: `${monthly}/month`,
			subline: null,
			button: `Join for ${isJoining ? first : monthly}`,
			checkout: trialStart
				? isJoining
					? `${trialStart}, then ${first} for the first month, then ${monthly} per month.`
					: `${trialStart}, then ${monthly} per month.`
				: null,
		};
	}

	const firstYearMonthly = isJoining
		? firstPayment + 11 * monthlyAmount
		: 12 * monthlyAmount;
	const savings = Math.max(0, Math.floor(firstYearMonthly - yearlyAmount));

	return {
		headline: `Join for ${yearly}/year`,
		subline:
			savings > 0
				? `${isJoining ? "No initial payment + " : ""}2 free months = Save ${format(savings)}`
				: null,
		button: `Join for ${yearly} per year`,
		checkout: trialStart ? `${trialStart}, then ${yearly} per year.` : null,
	};
};

/**
 * Copy for the plan in `planConfig.price`, using both offered prices for the
 * savings and FAQ lines. A promo code discounts the described plan only.
 *
 * @param {object} input
 * @param {PlanConfigExpanded} input.planConfig - Selection; pass `{ ...planConfig, price }` for a card
 * @param {readonly Stripe.Price[] | undefined} input.prices - Offered individual prices
 * @param {JoiningPriceOffer} input.offer - Visitor's arm
 * @param {string} [input.trialEndsLabel] - First billing date when the plan has a trial
 * @returns {JoiningPriceCopy | null} Copy, or null for team plans or missing prices
 */
export const getSelectedPlanJoiningCopy = (input: {
	planConfig: PlanConfigExpanded;
	prices: readonly Stripe.Price[] | undefined;
	offer: JoiningPriceOffer;
	trialEndsLabel?: string;
}): JoiningPriceCopy | null => {
	const { planConfig, prices, offer } = input;
	if (planConfig.planType !== PlanType.INDIVIDUAL) return null;

	const interval = planConfig.price?.recurring?.interval;
	const monthlyPrice = prices?.find(
		(price) => price.recurring?.interval === "month",
	);
	const yearlyPrice = prices?.find(
		(price) => price.recurring?.interval === "year",
	);
	if (
		!planConfig.price ||
		(interval !== "month" && interval !== "year") ||
		!monthlyPrice ||
		!yearlyPrice
	) {
		return null;
	}

	const amountFor = (price: Stripe.Price) =>
		price.id === planConfig.price?.id && planConfig.promoCode
			? getAmountIndividualAfterDiscount(price, planConfig.promoCode.coupon)
			: getAmountIndividual(price);

	return getJoiningPriceCopy({
		offer,
		interval,
		monthlyAmount: amountFor(monthlyPrice),
		yearlyAmount: amountFor(yearlyPrice),
		currency: planConfig.currency,
		trialEndsLabel: planConfig.trialLength ? input.trialEndsLabel : undefined,
	});
};

/**
 * @param {TrialLength | undefined} trialLength - Plan trial length
 * @returns {string | undefined} First billing date like "Nov 1, 2026", or undefined without a trial
 */
export const getTrialEndsLabel = (
	trialLength: TrialLength | undefined,
): string | undefined =>
	trialLength
		? format(new Date(getFirstBillingDate(trialLength)), "MMM d, yyyy")
		: undefined;

/**
 * Fee in dollars this checkout owes, now or when its trial ends. Only the
 * individual $39 monthly in USD carries a fee.
 *
 * @param {PlanConfigExpanded} planConfig - Current plan selection
 * @param {JoiningPriceOffer} offer - Visitor's arm
 * @returns {number} Fee in dollars, or 0
 */
export const getApplicableJoiningFee = (
	planConfig: PlanConfigExpanded,
	offer: JoiningPriceOffer,
): number => {
	if (planConfig.planType !== PlanType.INDIVIDUAL) return 0;
	if (planConfig.price?.id !== MEMBERSHIP_PRICE_IDS.month) return 0;
	const currency = planConfig.currency ?? planConfig.price.currency ?? "usd";
	if (currency.toLowerCase() !== "usd") return 0;
	return offer.joiningFee;
};

/**
 * @param {JoiningPriceOffer} offer - Visitor's arm
 * @returns {{ joining_price_variant: JoiningPriceVariant; joining_fee: number }} PostHog properties
 */
export const joiningPriceEventProperties = (offer: JoiningPriceOffer) => ({
	joining_price_variant: offer.variant,
	joining_fee: offer.joiningFee,
});

/**
 * @param {JoiningPriceOffer} offer - Visitor's arm
 * @returns {{ pricingModel: string; pricingVariant: JoiningPriceVariant }} Fields the API maps to a fee
 */
export const joiningPriceCheckoutFields = (offer: JoiningPriceOffer) => ({
	pricingModel: offer.model,
	pricingVariant: offer.variant,
});

const isJoiningPriceOffer = (value: unknown): value is JoiningPriceOffer => {
	if (!value || typeof value !== "object") return false;
	const offer = value as Partial<JoiningPriceOffer>;
	return (
		offer.model === MEMBERSHIP_JOINING_PRICE_MODEL &&
		isJoiningPriceVariant(offer.variant) &&
		offer.joiningFee === joiningFeeFor(offer.variant)
	);
};

/**
 * Stores the arm shown in the plan picker so events and the checkout request
 * outside React report the same one.
 *
 * @param {JoiningPriceOffer} offer - Visitor's arm
 */
export const setCurrentJoiningPriceOffer = (offer: JoiningPriceOffer) => {
	if (typeof sessionStorage === "undefined") return;
	sessionStorage.setItem(JOINING_PRICE_OFFER_KEY, JSON.stringify(offer));
};

/**
 * @returns {JoiningPriceOffer} The stored arm, or control when missing or tampered with
 */
export const getCurrentJoiningPriceOffer = (): JoiningPriceOffer => {
	if (typeof sessionStorage === "undefined") return CONTROL_JOINING_PRICE_OFFER;
	try {
		const stored: unknown = JSON.parse(
			sessionStorage.getItem(JOINING_PRICE_OFFER_KEY) ?? "null",
		);
		return isJoiningPriceOffer(stored) ? stored : CONTROL_JOINING_PRICE_OFFER;
	} catch {
		return CONTROL_JOINING_PRICE_OFFER;
	}
};
